import React, { useState } from 'react';
import { Inspection } from '../../services/inspectionsApi';
import { fmtDate, fmtDateTime } from '../../utils/format';

/** Visar åtgärden på en kontroll och, om den väntar, ett formulär för att klarmarkera den. */

interface Props {
    inspection: Inspection;
    /** Sätt när klarmarkering ska gå att göra. Namn skickas bara på den publika sidan. */
    onDone?: (note: string, name: string) => Promise<void>;
    /** Sätt när datumet för åtgärden ska gå att flytta. */
    onChangeDue?: (date: string) => Promise<void>;
    askName?: boolean;
    defaultName?: string;
}

const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6, padding: 9, fontSize: 15, fontFamily: 'inherit',
};


const ActionPanel: React.FC<Props> = ({ inspection: i, onDone, onChangeDue, askName = false, defaultName = '' }) => {
    const [note, setNote] = useState('');
    const [name, setName] = useState(defaultName);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Ändra datum: null = visas inte, annars datumet som redigeras.
    const [editDue, setEditDue] = useState<string | null>(null);
    const [savingDue, setSavingDue] = useState(false);
    const [dueError, setDueError] = useState<string | null>(null);
    if (!i.action_note) return null;

    const waiting = i.status === 'action';
    const late = waiting && !!i.action_due_date && i.action_due_date < new Date().toLocaleDateString('sv-SE');

    const submit = async () => {
        if (!onDone) return;
        if (!note.trim()) { setError('Beskriv vad som gjordes'); return; }
        if (askName && !name.trim()) { setError('Skriv ditt namn'); return; }
        setSaving(true); setError(null);
        try { await onDone(note.trim(), name.trim()); }
        catch (e: any) { setError(e?.response?.data?.error || e.message || 'Kunde inte spara'); }
        finally { setSaving(false); }
    };

    const saveDue = async () => {
        if (!onChangeDue || editDue == null) return;
        if (!editDue) { setDueError('Välj ett datum'); return; }
        setSavingDue(true); setDueError(null);
        try { await onChangeDue(editDue); setEditDue(null); }
        catch (e: any) { setDueError(e?.response?.data?.error || e.message || 'Kunde inte spara'); }
        finally { setSavingDue(false); }
    };

    return (
        <div style={{ marginTop: 12, padding: 14, borderRadius: 8, border: `1px solid ${waiting ? (late ? '#dc3545' : '#fd7e14') : '#c3e6cb'}`, background: waiting ? (late ? '#fff5f5' : '#fff8e6') : '#f0fff4' }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                {waiting ? (late ? 'Åtgärd försenad' : 'Åtgärd pågår') : 'Åtgärd klar'}
            </div>
            <div style={{ fontSize: 14 }}><b>Att göra:</b> {i.action_note}</div>
            <div style={{ fontSize: 13, color: '#555' }}>
                Senast {fmtDate(i.action_due_date)} · anmält av {i.completed_by_name ?? 'okänd'}
                {waiting && onChangeDue && editDue == null && (
                    <> · <button type="button" onClick={() => setEditDue(i.action_due_date ?? '')} style={{ background: 'none', border: 'none', padding: 0, color: '#1a4aa6', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', textDecoration: 'underline' }}>Ändra datum</button></>
                )}
            </div>
            {waiting && onChangeDue && editDue != null && (
                <div style={{ marginTop: 8 }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Ska vara klart senast</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input type="date" value={editDue} onChange={e => setEditDue(e.target.value)} style={{ ...input, width: 'auto', maxWidth: 220 }} />
                        <button type="button" onClick={saveDue} disabled={savingDue} style={{ background: '#0f3460', color: 'white', border: 'none', borderRadius: 6, padding: '9px 14px', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
                            {savingDue ? 'Sparar…' : 'Spara datum'}
                        </button>
                        <button type="button" onClick={() => { setEditDue(null); setDueError(null); }} style={{ background: 'none', border: '1px solid #ccc', borderRadius: 6, padding: '8px 12px', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', color: '#333' }}>Avbryt</button>
                    </div>
                    {dueError && <div style={{ background: '#f8d7da', color: '#721c24', padding: 8, borderRadius: 6, marginTop: 8 }}>{dueError}</div>}
                </div>
            )}
            {!waiting && (
                <div style={{ fontSize: 14, marginTop: 6 }}>
                    <b>Gjort:</b> {i.action_done_note}
                    <div style={{ fontSize: 13, color: '#555' }}>{fmtDateTime(i.action_done_at)} av {i.action_done_by_name ?? 'okänd'}</div>
                </div>
            )}
            {waiting && onDone && (
                <div style={{ marginTop: 10 }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Vad gjordes? *</label>
                    <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} style={input} />
                    {askName && (
                        <>
                            <label style={{ display: 'block', fontWeight: 600, fontSize: 14, margin: '10px 0 4px' }}>Ditt namn *</label>
                            <input value={name} onChange={e => setName(e.target.value)} style={input} />
                        </>
                    )}
                    {error && <div style={{ background: '#f8d7da', color: '#721c24', padding: 8, borderRadius: 6, marginTop: 8 }}>{error}</div>}
                    <button type="button" onClick={submit} disabled={saving} style={{ marginTop: 10, background: '#28a745', color: 'white', border: 'none', borderRadius: 6, padding: '10px 18px', fontSize: 15, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 'bold' }}>
                        {saving ? 'Sparar…' : 'Klarmarkera åtgärden'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ActionPanel;
